import {
  prefill_question_value,
  build_repeatable_groups,
  create_blank_runtime_sub_section_instance,
  create_runtime_sub_section_instance
} from './queue_form_helpers';
import {
  create_repeat_group_key
} from './queue_form_method_shared';
import { read_safe_object_value } from '@/services/onboarding_form_helpers';

export const queueFormRepeatableMethods = {
  prefill_default_answers() {
    this.intake_steps.forEach((main_section) => {
      main_section.sub_sections.forEach((sub_section) => {
        sub_section.questions.forEach((question) => {
          prefill_question_value({
            question,
            form_values: this.form_values,
            file_values: this.file_values,
            persisted_file_urls: this.persisted_file_urls
          });
        });
      });
    });
  },
  buildRepeatableGroups() {
    return build_repeatable_groups(this.intake_steps);
  },
  can_duplicate_sub_section(sub_section = {}) {
    return this.can_edit_sub_section(sub_section)
      && Array.isArray(sub_section?.questions)
      && sub_section.questions.length > 0;
  },
  can_remove_sub_section(sub_section = {}) {
    const base_section_id = String(sub_section?.base_section_id || sub_section?.id || '');
    if (!base_section_id || !this.current_main_section) return false;
    return this.current_main_section.sub_sections.filter(
      (item) => String(item?.base_section_id || item?.id || '') === base_section_id
    ).length > 1;
  },
  format_sub_section_title(sub_section = {}) {
    const label = String(sub_section?.label || '').trim();
    const instance_number = Number(sub_section?.instance_number || 1);
    return instance_number > 1 ? `${label} ${instance_number}` : label;
  },
  move_question_state(question, next_question_id) {
    const previous_question_id = String(question?.id || '');
    const normalized_next_question_id = String(next_question_id || '');
    if (!previous_question_id || !normalized_next_question_id || previous_question_id === normalized_next_question_id) return;

    const form_value = read_safe_object_value(this.form_values, previous_question_id, undefined);
    if (form_value !== undefined) {
      Reflect.set(this.form_values,normalized_next_question_id,form_value);
      Reflect.deleteProperty(this.form_values, previous_question_id);
    }
    const file_value = read_safe_object_value(this.file_values, previous_question_id, undefined);
    if (file_value !== undefined) {
      Reflect.set(this.file_values,normalized_next_question_id,file_value);
      Reflect.deleteProperty(this.file_values, previous_question_id);
    }
    const preview_value = read_safe_object_value(this.file_previews, previous_question_id, undefined);
    if (preview_value !== undefined) {
      Reflect.set(this.file_previews,normalized_next_question_id,preview_value);
      Reflect.deleteProperty(this.file_previews, previous_question_id);
    }
    const persisted_file = read_safe_object_value(this.persisted_file_urls, previous_question_id, undefined);
    if (persisted_file !== undefined) {
      Reflect.set(this.persisted_file_urls,normalized_next_question_id,persisted_file);
      Reflect.deleteProperty(this.persisted_file_urls, previous_question_id);
    }
  },
  assign_repeat_group_key_to_sub_section(sub_section, answer_group_key, instance_number = 1) {
    if (!sub_section) return;
    const runtime_sub_section = create_runtime_sub_section_instance(sub_section, answer_group_key, instance_number);
    Reflect.set(sub_section, 'answer_group_key', runtime_sub_section.answer_group_key);
    Reflect.set(sub_section, 'instance_id', runtime_sub_section.instance_id);
    Reflect.set(sub_section, 'instance_number', runtime_sub_section.instance_number);
    const current_questions = Array.isArray(sub_section.questions) ? sub_section.questions : [];
    const next_questions = runtime_sub_section.questions.map((next_question, index) => {
      const current_question = Reflect.get(current_questions, index);
      this.move_question_state(current_question, next_question.id);
      return { ...current_question, ...next_question };
    });
    Reflect.set(sub_section, 'questions', next_questions);
  },
  add_sub_section_instance(sub_section) {
    if (!this.current_main_section || !this.can_duplicate_sub_section(sub_section)) return;
    const base_section_id = String(sub_section?.base_section_id || sub_section?.id || '');
    const related_sub_sections = this.current_main_section.sub_sections.filter(
      (item) => String(item?.base_section_id || item?.id || '') === base_section_id
    );
    if (!related_sub_sections.length) return;

    related_sub_sections.forEach((item, index) => {
      if (item.answer_group_key) {
        item.instance_number = index + 1;
        return;
      }
      this.assign_repeat_group_key_to_sub_section(item, create_repeat_group_key(), index + 1);
    });

    const duplicate_sub_section = create_blank_runtime_sub_section_instance(
      related_sub_sections[0],
      create_repeat_group_key(),
      related_sub_sections.length + 1
    );
    duplicate_sub_section.questions.forEach((question) => {
      prefill_question_value({
        question,
        form_values: this.form_values,
        file_values: this.file_values,
        persisted_file_urls: this.persisted_file_urls
      });
    });

    const insert_after_index = this.current_main_section.sub_sections.reduce(
      (last_match_index, item, index) => (
        String(item?.base_section_id || item?.id || '') === base_section_id ? index : last_match_index
      ),
      -1
    );
    this.current_main_section.sub_sections.splice(insert_after_index + 1, 0, duplicate_sub_section);
  },
  remove_sub_section_instance(sub_section) {
    if (!this.current_main_section || !this.can_remove_sub_section(sub_section)) return;
    sub_section.questions.forEach((question) => {
      const question_id = String(question?.id || '');
      const preview_url = read_safe_object_value(this.file_previews, question_id, '');
      if (preview_url) URL.revokeObjectURL(preview_url);
      Reflect.deleteProperty(this.form_values, question_id);
      Reflect.deleteProperty(this.file_values, question_id);
      Reflect.deleteProperty(this.file_previews, question_id);
      Reflect.deleteProperty(this.persisted_file_urls, question_id);
    });

    this.current_main_section.sub_sections = this.current_main_section.sub_sections.filter(
      (item) => item.instance_id !== sub_section.instance_id
    );
    const base_section_id = String(sub_section?.base_section_id || sub_section?.id || '');
    this.current_main_section.sub_sections
      .filter((item) => String(item?.base_section_id || item?.id || '') === base_section_id)
      .forEach((item, index) => {
        item.instance_number = index + 1;
      });
  },
  is_active_step(step) {
    return step.number === this.current_step;
  },
  can_view_sub_section(sub_section = {}) {
    const access = sub_section?.access;
    if (!access || typeof access !== 'object') return true;
    return access.can_view !== false;
  },
  can_edit_sub_section(sub_section = {}) {
    return this.can_view_sub_section(sub_section);
  },
  sub_section_access_note(sub_section = {}) {
    const access = sub_section?.access;
    if (!access || typeof access !== 'object') return '';
    const note = String(access.note || '').trim();
    if (note.toLowerCase() === 'no specific access rules found for this section') return '';
    return note;
  },
  is_question_read_only(sub_section = {}) {
    return !this.can_edit_sub_section(sub_section);
  },
  go_to_step(step_number) {
    const normalized_step = Number(step_number || 0);
    if (normalized_step < 1 || normalized_step > this.intake_steps.length) return;
    this.current_step = normalized_step;
    this.form_page_error = '';
    this.$nextTick(() => this.scroll_active_step_into_view());
  },
  scroll_steps(direction = 'right') {
    const steps_scroller = this.$refs.stepsScroller;
    if (!steps_scroller) return;
    const scroll_offset = Math.max(180, Math.round(steps_scroller.clientWidth * 0.65));
    const left = direction === 'left' ? steps_scroller.scrollLeft - scroll_offset : steps_scroller.scrollLeft + scroll_offset;
    steps_scroller.scrollTo({ left, behavior: 'smooth' });
  },
  scroll_active_step_into_view() {
    const steps_scroller = this.$refs.stepsScroller;
    const active_step_element = steps_scroller?.querySelector?.(`[data-step-number="${this.current_step}"]`);
    if (!steps_scroller || !active_step_element) return;

    active_step_element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    const active_rect = active_step_element.getBoundingClientRect();
    const scroller_rect = steps_scroller.getBoundingClientRect();
    if (active_rect.left >= scroller_rect.left && active_rect.right <= scroller_rect.right) return;

    steps_scroller.scrollTo({
      left: Math.max(0, active_step_element.offsetLeft - ((steps_scroller.clientWidth - active_step_element.clientWidth) / 2)),
      behavior: 'smooth'
    });
  },
  go_to_next_step() {
    if (this.current_step < this.intake_steps.length) {
      this.current_step += 1;
      this.$nextTick(() => this.scroll_active_step_into_view());
    }
  }
};
